import { Box, Chip, Divider, Stack, Typography } from "@mui/joy";
import type { CommissionCalculation } from "../../types/commissionStructure";

const commissionMoney = (value: string | number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(Number(value));
const basisLabels = {
  contract: "gross contract",
  profit: "net profit",
  remaining: "remaining balance",
};

export function CommissionBreakdown({
  calculation: c,
  sample = false,
}: {
  calculation: CommissionCalculation;
  sample?: boolean;
}) {
  const row = (label: string, value: string, strong = false) => (
    <Stack
      direction="row"
      justifyContent="space-between"
      gap={2}
      sx={{ py: 0.9 }}
    >
      <Typography level={strong ? "title-sm" : "body-sm"}>{label}</Typography>
      <Typography
        level="title-sm"
        sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
      >
        {value}
      </Typography>
    </Stack>
  );
  const total = Math.max(1, Number(c.contract));
  return (
    <Stack gap={1} data-testid="commission-breakdown">
      <Box
        sx={{ bgcolor: "#241345", color: "#fff", p: 2.5, borderRadius: "lg" }}
      >
        <Typography
          level="body-xs"
          sx={{
            color: "#ddd1f5",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
          }}
        >
          {sample ? "Sample job · live preview" : "Job commission calculation"}
        </Typography>
        <Typography level="h2" sx={{ color: "#fff", mt: 0.5 }}>
          {commissionMoney(c.contract)}
        </Typography>
        <Typography level="body-sm" sx={{ color: "#ddd1f5" }}>
          Gross contract amount
        </Typography>
        <Box
          sx={{
            display: "flex",
            height: 8,
            borderRadius: 8,
            overflow: "hidden",
            mt: 2,
            bgcolor: "#ffffff20",
          }}
          aria-hidden="true"
        >
          {[
            ["#b5a6c7", c.net_costs],
            ["#a78bfa", c.company_amount],
            ["#4ade80", c.rep_amount],
          ].map(([color, value]) => (
            <Box
              key={color}
              sx={{
                bgcolor: color,
                width: `${(Math.max(0, Number(value)) / total) * 100}%`,
              }}
            />
          ))}
        </Box>
        <Stack direction="row" gap={2} sx={{ mt: 1 }}>
          <Typography level="body-xs" sx={{ color: "#b5a6c7" }}>
            ● Costs
          </Typography>
          <Typography level="body-xs" sx={{ color: "#c4b5fd" }}>
            ● Company
          </Typography>
          <Typography level="body-xs" sx={{ color: "#86efac" }}>
            ● Rep
          </Typography>
        </Stack>
      </Box>
      <Box sx={{ px: 1 }}>
        {row("Job costs", `− ${commissionMoney(c.costs)}`)}
        {row("Credits returned to the job", `+ ${commissionMoney(c.credits)}`)}
        <Divider />
        {row("Net profit before commission", commissionMoney(c.profit), true)}
        <Divider />
        {c.rules.map((rule, i) => (
          <Box
            key={i}
            sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider" }}
          >
            <Stack direction="row" justifyContent="space-between" gap={1}>
              <Typography level="title-sm">
                {i + 1}. {rule.label}
              </Typography>
              <Typography level="title-sm" sx={{ whiteSpace: "nowrap" }}>
                {commissionMoney(rule.amount)}
              </Typography>
            </Stack>
            <Typography level="body-xs" sx={{ mt: 0.4 }}>
              {rule.method === "fixed"
                ? "Fixed amount"
                : `${Number(rule.rate)}% × ${commissionMoney(rule.basis_amount)} ${basisLabels[rule.basis]}`}
              {rule.method === "rep_rate" ? " · individual rate" : ""}
            </Typography>
            {rule.limited && (
              <Chip size="sm" color="warning" sx={{ mt: 0.5 }}>
                Limited to available profit
              </Chip>
            )}
          </Box>
        ))}
        {row(
          "Remaining balance → company",
          commissionMoney(c.company_remainder),
        )}
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
        <Box sx={{ p: 2, bgcolor: "neutral.softBg", borderRadius: "md" }}>
          <Typography level="body-xs">Company keeps</Typography>
          <Typography level="h3">
            {commissionMoney(c.company_amount)}
          </Typography>
          <Typography level="body-xs">Retention + remainder</Typography>
        </Box>
        <Box sx={{ p: 2, bgcolor: "success.softBg", borderRadius: "md" }}>
          <Typography level="body-xs">Sales rep earns</Typography>
          <Typography level="h3" color="success">
            {commissionMoney(c.rep_amount)}
          </Typography>
          <Typography level="body-xs">All rep rules combined</Typography>
        </Box>
      </Box>
      {!c.has_rep && (
        <Typography color="warning" level="body-sm">
          Assign a sales rep to calculate their commission.
        </Typography>
      )}
      {Number(c.company_amount) < 0 && (
        <Typography color="warning" level="body-sm">
          The company has a loss after commission. Review costs and payout
          rules.
        </Typography>
      )}
    </Stack>
  );
}
